/**
 * Stage 46a — Runtime theme bootstrap.
 *
 * The frontend fetches the admin-configured design token
 * overrides at boot, injects them as a <style id="cos-runtime-theme">
 * block on <head>, and listens for `theme-changed` events to
 * re-apply when admins save new values.
 *
 * Defaults from design-system/tokens.css always work — this
 * module's failure modes are silent (the app boots with the
 * baked-in theme).
 *
 * The contract with the admin settings page is the global
 * `theme-changed` window event. After a successful PATCH, the
 * settings page dispatches it; we refetch + reapply without a
 * page reload.
 */

import { api } from './api.js';

const STYLE_ID = 'cos-runtime-theme';

export type ThemeMap = Record<string, string>;

/**
 * Fetch + apply the runtime theme. Call once during app boot
 * BEFORE the first route renders. Returns the applied override
 * map (empty if the fetch failed — the app stays on defaults).
 *
 * Safe to call concurrently with renders — the cascade lets
 * the runtime block override any defaults already in tokens.css.
 */
export async function bootstrapTheme(): Promise<ThemeMap> {
	try {
		const overrides = (await api.getDesignSettings()) as ThemeMap;
		applyTheme(overrides ?? {});
		return overrides ?? {};
	} catch {
		// Network blip / backend down — boot on the defaults
		// in tokens.css. Never block the UI on the theme.
		return {};
	}
}

/**
 * Inject (or remove) a `<style id="cos-runtime-theme">` block
 * that sets the supplied custom properties on :root.
 *
 * Idempotent: removes the previous block before writing the
 * new one. An empty map removes the block entirely (so the
 * defaults from tokens.css are what render).
 */
export function applyTheme(overrides: ThemeMap): void {
	if (typeof document === 'undefined') return;
	document.getElementById(STYLE_ID)?.remove();
	const keys = Object.keys(overrides);
	if (keys.length === 0) return;
	const style = document.createElement('style');
	style.id = STYLE_ID;
	const declarations = keys
		.map(k => `  ${k}: ${overrides[k]};`)
		.join('\n');
	style.textContent = `:root {\n${declarations}\n}`;
	document.head.appendChild(style);
}

/**
 * Read the current override map by parsing the runtime style
 * block. Used by tests + the admin settings page to inspect
 * what's currently applied without an extra fetch.
 */
export function getAppliedTheme(): ThemeMap {
	if (typeof document === 'undefined') return {};
	const el = document.getElementById(STYLE_ID);
	if (!el || !el.textContent) return {};
	const out: ThemeMap = {};
	for (const line of el.textContent.split('\n')) {
		const m = line.match(/\s*(--[a-z0-9-]+):\s*(.+?);/i);
		if (m) out[m[1]] = m[2].trim();
	}
	return out;
}

let bound = false;

/**
 * Wire up the `theme-changed` window event so the admin
 * settings page can trigger a re-bootstrap without reloading.
 * Idempotent — calling more than once is a no-op.
 */
export function bindThemeChangeListener(): void {
	if (bound || typeof window === 'undefined') return;
	bound = true;
	window.addEventListener('theme-changed', () => {
		void bootstrapTheme();
	});
}

/**
 * Convenience helper for the admin settings page — dispatch
 * the event without having to reach into window.
 */
export function notifyThemeChanged(): void {
	if (typeof window === 'undefined') return;
	window.dispatchEvent(new CustomEvent('theme-changed'));
}

/* ===================================================================
 * Stage 85a — Quiet user theme (mode + palette).
 *
 * Distinct from the Stage 46a ADMIN runtime override above: this is
 * the per-USER light/dark + palette choice. It sets data-mode +
 * data-pal on document.documentElement (NOT #cos-app — every --cos-*
 * token block keys on :root, matching the data-theme/data-density
 * precedent). The admin override <style> still wins by source order
 * because the palette blocks in tokens.css are :where()-wrapped to
 * stay at :root specificity.
 * =================================================================== */

export type QuietMode = 'light' | 'dark';
export type LightPalette = 'mist' | 'sage' | 'linen';
export type DarkPalette = 'slate' | 'charcoal' | 'ink';

export interface UserTheme {
	mode: QuietMode;
	light: LightPalette;
	dark: DarkPalette;
}

export const LIGHT_PALETTES: LightPalette[] = ['mist', 'sage', 'linen'];
export const DARK_PALETTES: DarkPalette[] = ['slate', 'charcoal', 'ink'];

/** Defaults per DESIGN-SYSTEM.md §2.2: light/Mist, dark default Slate. */
export const DEFAULT_THEME: UserTheme = { mode: 'light', light: 'mist', dark: 'slate' };

/** Human label for a palette key (Sage/Linen are the Quiet labels for the
 *  legacy greige/tea keys — we use the clean keys; labels are display-only). */
export const PALETTE_LABELS: Record<string, string> = {
	mist: 'Mist', sage: 'Sage', linen: 'Linen',
	slate: 'Slate', charcoal: 'Charcoal', ink: 'Ink',
};

/** True if the OS currently prefers dark. */
export function prefersDark(): boolean {
	return (
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-color-scheme: dark)').matches
	);
}

/** First-run default: the canonical defaults, with mode seeded from the OS. */
export function osDefaultTheme(): UserTheme {
	return { ...DEFAULT_THEME, mode: prefersDark() ? 'dark' : 'light' };
}

/** Coerce an unknown (e.g. a stored preference of unknown shape) into a valid
 *  UserTheme — invalid/missing fields fall back to the canonical defaults so a
 *  corrupt preference can never brick the theme. */
export function normalizeTheme(v: unknown): UserTheme {
	const o = (v && typeof v === 'object') ? (v as Record<string, unknown>) : {};
	const mode: QuietMode = o.mode === 'dark' ? 'dark' : 'light';
	const light = LIGHT_PALETTES.includes(o.light as LightPalette) ? (o.light as LightPalette) : DEFAULT_THEME.light;
	const dark = DARK_PALETTES.includes(o.dark as DarkPalette) ? (o.dark as DarkPalette) : DEFAULT_THEME.dark;
	return { mode, light, dark };
}

/** Apply the active mode + palette to documentElement. The palette attribute
 *  is the palette for the ACTIVE mode (so a dark/Slate root carries
 *  data-pal="slate", not the stored light palette). */
export function applyUserTheme(theme: UserTheme): void {
	if (typeof document === 'undefined') return;
	const root = document.documentElement;
	root.setAttribute('data-mode', theme.mode);
	root.setAttribute('data-pal', theme.mode === 'dark' ? theme.dark : theme.light);
}

/** Read the theme currently applied to documentElement (defaults fill gaps).
 *  Used by the appearance settings component as the source of truth. */
export function readAppliedTheme(): UserTheme {
	if (typeof document === 'undefined') return { ...DEFAULT_THEME };
	const root = document.documentElement;
	const mode: QuietMode = root.getAttribute('data-mode') === 'dark' ? 'dark' : 'light';
	const pal = root.getAttribute('data-pal') ?? '';
	const theme: UserTheme = { ...DEFAULT_THEME, mode };
	if (mode === 'dark' && DARK_PALETTES.includes(pal as DarkPalette)) theme.dark = pal as DarkPalette;
	if (mode === 'light' && LIGHT_PALETTES.includes(pal as LightPalette)) theme.light = pal as LightPalette;
	return theme;
}
